/**
 * Utility functions for document display formatting
 */

/**
 * Format document title to avoid double display when title equals fileName.
 *
 * Logic:
 * - If title and fileName are the same (case-insensitive), return title only
 * - If title equals fileName without extension, return title only
 * - Otherwise, return "title - fileName" format
 *
 * @param title - LLM-generated or document title
 * @param fileName - Original file name
 * @returns Formatted title string
 *
 * @example
 * formatDocumentTitle('Report.PDF', 'report.pdf') // => 'Report.PDF'
 * formatDocumentTitle('設計基準書', '設計基準書.pdf') // => '設計基準書'
 * formatDocumentTitle('ビス締結基準', 'standard_001.pdf') // => 'ビス締結基準 - standard_001.pdf'
 */
export const formatDocumentTitle = (title: string | undefined | null, fileName: string | undefined | null): string => {
  // Handle edge cases
  if (!title && !fileName) {
    return '';
  }
  if (!title) {
    return fileName || '';
  }
  if (!fileName) {
    return title;
  }

  // Normalize for comparison (trim whitespace, lowercase)
  const normalizedTitle = title.trim().toLowerCase();
  const normalizedFileName = fileName.trim().toLowerCase();

  // Case 1: Title equals fileName (case-insensitive)
  if (normalizedTitle === normalizedFileName) {
    return title;
  }

  // Case 2: Title equals fileName without extension
  const fileNameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
  const normalizedFileNameWithoutExt = fileNameWithoutExt.trim().toLowerCase();

  if (normalizedTitle === normalizedFileNameWithoutExt) {
    return title;
  }

  // Case 3: Title and fileName are different - show both
  return `${title} - ${fileName}`;
};

/**
 * Get display title for document, with optional subtitle.
 * This is useful when you want to display title and fileName separately but
 * avoid redundancy.
 *
 * @param title - LLM-generated or document title
 * @param fileName - Original file name
 * @returns Object with main title and optional subtitle
 */
export const getDocumentDisplayParts = (
  title: string | undefined | null,
  fileName: string | undefined | null
): { main: string; subtitle: string | null } => {
  // Handle edge cases
  if (!title && !fileName) {
    return { main: '', subtitle: null };
  }
  if (!title) {
    return { main: fileName || '', subtitle: null };
  }
  if (!fileName) {
    return { main: title, subtitle: null };
  }

  // Normalize for comparison
  const normalizedTitle = title.trim().toLowerCase();
  const normalizedFileName = fileName.trim().toLowerCase();
  const fileNameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
  const normalizedFileNameWithoutExt = fileNameWithoutExt.trim().toLowerCase();

  // If title equals fileName or fileName without extension, no subtitle needed
  if (normalizedTitle === normalizedFileName || normalizedTitle === normalizedFileNameWithoutExt) {
    return { main: title, subtitle: null };
  }

  // Different - show title as main, fileName as subtitle
  return { main: title, subtitle: fileName };
};

// Kana, CJK ideographs (incl. extension A and compatibility) and halfwidth kana.
const CJK = /[぀-ヿ㐀-䶿一-鿿豈-﫿ｦ-ﾟ]/;

const firstGrapheme = (value: string): string => Array.from(value)[0] ?? '';

/**
 * Derive up to two display characters from a person or entity name.
 *
 * A CJK name carries no spaces, so the Latin "first letter of each word" rule
 * truncates it mid-word: '不明' would render as '不'. CJK therefore keeps its
 * leading two characters, which is the family name for a real name and a whole
 * word for a placeholder.
 *
 * @param name - Full name, may be missing
 * @returns 0-2 characters, empty when there is no usable name
 *
 * @example
 * getInitials('Vickey Kumar') // => 'VK'
 * getInitials('山田太郎')       // => '山田'
 * getInitials('不明')          // => '不明'
 */
export const getInitials = (name: string | undefined | null): string => {
  const trimmed = name?.trim();
  if (!trimmed) {
    return '';
  }

  if (CJK.test(trimmed)) {
    return Array.from(trimmed.replace(/\s+/g, '')).slice(0, 2).join('');
  }

  const tokens = trimmed.split(/\s+/);
  const last = tokens.length > 1 ? firstGrapheme(tokens[tokens.length - 1]) : '';
  return (firstGrapheme(tokens[0]) + last).toUpperCase();
};
